/**
 * A funding round: what it was called, when it closed, what a share cost.
 *
 * Everything else about it — shares issued, money raised, how many investors —
 * is counted from the purchases that name it, so there is nothing here to keep
 * in sync and no figure that can contradict the register.
 */
import { useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { parseKr } from '@/shared/util/money'
import type { Attachment, Issuance } from '@/types/ownership'
import { AttachmentsField } from './AttachmentsField'
import '@/shared/ui/RecordForm.css'

interface Props {
  issuance: Issuance
  isNew: boolean
  /**
   * Holders in this round — with interest while it is open, with shares once it
   * has closed. It cannot be deleted while they exist.
   */
  investorNames: string[]
  /** Opens the payment request to this round's subscribers. */
  onRequestPayment: () => void
  /** Opens the generated board minutes for this increase. */
  onBoardMinutes: () => void
  onClose: () => void
  onSave: (draft: Omit<Issuance, 'id' | 'createdAt' | 'updatedAt'>, id?: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

export function IssuanceModal({
  issuance,
  isNew,
  investorNames,
  onRequestPayment,
  onBoardMinutes,
  onClose,
  onSave,
  onDelete,
}: Props) {
  const [form, setForm] = useState<Issuance>(issuance)
  /**
   * The price as typed, kept as a string until Save.
   *
   * It used to be bound straight to the parsed number, which fought the person
   * typing: `Number('166,')` is `NaN` and cleared the field, and even
   * `Number('166.')` is `166`, so the separator vanished the moment it was
   * pressed and a price could never reach its decimals. A share price needs
   * them — `166,67` is an ordinary result of dividing a round sum by a share
   * count — and Norwegian keyboards produce a comma.
   */
  const [price, setPrice] = useState(
    issuance.pricePerShare === null ? '' : String(issuance.pricePerShare).replace('.', ','),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Held apart from `form` because an attachment is already filed by the time
   * it appears here — the upload happened when the file was picked. Saving the
   * round records which files belong to it; it does not move any bytes.
   */
  const [attachments, setAttachments] = useState<Attachment[]>(issuance.attachments ?? [])

  const set = (patch: Partial<Issuance>) => setForm((f) => ({ ...f, ...patch }))

  const save = async () => {
    if (!form.name.trim()) {
      setError('A round needs a name.')
      return
    }
    // Comma or point, either is a decimal separator here.
    const parsed = price.trim() === '' ? null : parseKr(price)
    if (price.trim() !== '' && parsed === null) {
      setError('That price per share is not a number.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const { id: _id, createdAt: _c, updatedAt: _u, ...draft } = form
      void _id
      void _c
      void _u
      await onSave(
        { ...draft, name: draft.name.trim(), pricePerShare: parsed, attachments },
        isNew ? undefined : issuance.id,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={isNew ? 'New issuance' : form.name || 'Issuance'}
      onClose={onClose}
      width={440}
      footer={
        <>
          {!isNew && investorNames.length === 0 && (
            <button
              className="btn btn--ghost recform__delete"
              disabled={saving}
              onClick={() => void onDelete(issuance.id)}
            >
              Delete issuance
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save issuance'}
          </button>
        </>
      }
    >
      <div className="recform">
        <section className="recform__group">
          <div className="recform__grid">
            <label className="field recform__wide">
              <span>Name</span>
              <input
                className="input"
                value={form.name}
                placeholder="e.g. Series B"
                onChange={(e) => set({ name: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Date</span>
              <input
                className="input"
                type="date"
                value={form.date}
                onChange={(e) => set({ date: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Status</span>
              <select
                className="input"
                value={form.status ?? 'completed'}
                onChange={(e) => set({ status: e.target.value as Issuance['status'] })}
              >
                <option value="completed">Completed</option>
                <option value="ongoing">Ongoing</option>
              </select>
            </label>
            {/*
              Reported to Foretaksregisteret — 2 Sep 2026.

              A date rather than a checkbox, because the aksjeeierbevis has to
              print the day the increase was registered, and a tick would have
              meant coming back for the date later. Clearing it empties the
              field rather than writing undefined, which Firestore refuses.
            */}
            <label className="field">
              <span>Reported to Foretaksregisteret</span>
              <input
                className="input"
                type="date"
                value={form.registeredAt ?? ''}
                onChange={(e) => set({ registeredAt: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Price per share (kr)</span>
              <input
                className="input"
                inputMode="decimal"
                value={price}
                placeholder="166,67"
                onChange={(e) => setPrice(e.target.value)}
              />
            </label>
          </div>
          {/* What the date above unlocks, said where it is set. Until a round is
              reported, its shareholders have no certificate to fetch. */}
          <p className="recform__note">
            {form.registeredAt
              ? `Reported ${form.registeredAt}, so shareholders in this round can take a share certificate for their shares.`
              : 'Not yet reported. A shareholder can take a receipt for money they have paid, and a share certificate only once the increase is registered.'}
          </p>
          <p className="recform__note">
            {form.status === 'ongoing'
              ? 'An ongoing round issues nothing. Interest and signed subscription forms are recorded against it and counted nowhere — not in a holding, not in shares outstanding, not in anybody’s percentage — until the round closes and the money arrives.'
              : 'A completed round has issued its shares: they are purchases on the holders and they carry the percentages.'}
          </p>

          {/* Deleting a round with shares against it would leave every purchase
              naming it pointing at nothing, while the percentages kept counting
              them. Say who is in the way rather than hiding the button. */}
          {!isNew && investorNames.length > 0 && (
            <p className="recform__note">
              {investorNames.length} shareholder{investorNames.length === 1 ? '' : 's'}{' '}
              {issuance.status === 'ongoing'
                ? `${investorNames.length === 1 ? 'has' : 'have'} registered interest in`
                : `${investorNames.length === 1 ? 'holds' : 'hold'} shares from`}{' '}
              this round ({investorNames.slice(0, 3).join(', ')}
              {investorNames.length > 3 ? ', …' : ''}), so it cannot be deleted.
            </p>
          )}
        </section>

        {/* Both documents a round produces, on the round itself — decision 19
            keeps them here rather than on the card, which shows numbers and
            status only. Neither reads unsaved changes: both are generated from
            the register. */}
        {!isNew && investorNames.length > 0 && (
          <section className="recform__group">
            <h4 className="recform__legend">Documents</h4>
            <div className="recform__docs">
              <button className="btn btn--outline" onClick={onRequestPayment} disabled={saving}>
                Payment request
              </button>
              <button className="btn btn--outline" onClick={onBoardMinutes} disabled={saving}>
                Board minutes
              </button>
            </div>
            <p className="recform__note">
              {issuance.paymentRequestSentAt
                ? `Payment request last sent ${issuance.paymentRequestSentAt.slice(0, 10)}${
                    issuance.paymentRequestCount
                      ? ` to ${issuance.paymentRequestCount} recipients`
                      : ''
                  }. Sending again writes to everybody picked, including anyone who has already paid.`
                : investorNames.length === 1
                  ? `The single subscriber is asked for their own amount, computed from what they ${issuance.status === 'ongoing' ? 'subscribed for' : 'hold'} in this round. The minutes are generated in Norwegian from the same figures.`
                  : `Each of the ${investorNames.length} subscribers is asked for their own amount, computed from what they ${issuance.status === 'ongoing' ? 'subscribed for' : 'hold'} in this round. The minutes are generated in Norwegian from the same figures.`}
            </p>
          </section>
        )}

        <AttachmentsField
          scope="issuances"
          recordId={isNew ? undefined : issuance.id}
          attachments={attachments}
          onChange={setAttachments}
        />

        {error && <p className="recform__error">{error}</p>}
      </div>
    </Modal>
  )
}

export const emptyIssuance = (): Issuance => ({ id: '', name: '', date: '', pricePerShare: null })
